import { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { devLog } from '../utils/devLog';

// Send httpOnly auth cookie with every request
axios.defaults.withCredentials = true;

export function useAuth() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const [plants, setPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [rates, setRates] = useState(null);
  const [permissions, setPermissions] = useState({});

  const loadPermissions = useCallback(async (authToken) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.get(`${API}/auth/permissions`, { headers });
      setPermissions(res.data.tabs || {});
    } catch (err) {
      devLog.warn('loadPermissions failed:', err?.message);
      setPermissions({});
    }
  }, []);

  const loadPlantRates = useCallback(async (plantId, authToken) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const ratesRes = await axios.get(`${API}/plants/${plantId}/rates`, { headers });
      setRates(ratesRes.data);
    } catch (err) {
      devLog.warn('loadPlantRates failed (optional):', err?.message);
    }
  }, []);

  const loadPlants = useCallback(async (authToken) => {
    try {
      setLoading(true);
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const plantsRes = await axios.get(`${API}/plants`, { headers });
      setPlants(plantsRes.data);
      if (plantsRes.data.length > 0) {
        setSelectedPlant(plantsRes.data[0]);
        loadPlantRates(plantsRes.data[0].id, authToken);
      }
    } catch (err) {
      devLog.warn('loadPlants failed (user may have no plants):', err?.message);
    } finally {
      setLoading(false);
    }
  }, [loadPlantRates]);

  const loadUserData = useCallback(async (authToken) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const userRes = await axios.get(`${API}/auth/me`, { headers });
      setUser(userRes.data);
      setIsLoggedIn(true);
      if (authToken) setToken(authToken);
      loadPlants(authToken);
      loadPermissions(authToken);
    } catch (err) {
      devLog.info('loadUserData: no valid session, showing login screen');
      setIsLoggedIn(false);
      setUser(null);
      setPermissions({});
    }
  }, [loadPlants, loadPermissions]);

  const handleLogin = async (username, password) => {
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username.trim());
      formData.append('password', password);
      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      const authToken = response.data.access_token;
      setToken(authToken);
      await loadUserData(authToken);
    } finally {
      // Always release the loading state — even when the server returns 401
      // so the user can retry without refreshing.
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      devLog.warn('logout call failed (clearing client state regardless):', err?.message);
    }
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setPlants([]);
    setSelectedPlant(null);
    setRates(null);
    setPermissions({});
  };

  return {
    isLoggedIn, token, user, loading, setLoading,
    plants, setPlants, selectedPlant, setSelectedPlant,
    rates, setRates, permissions,
    loadUserData, loadPlants, loadPlantRates,
    handleLogin, handleLogout,
  };
}
